import React, { useState, useEffect } from "react";
import "./HeroSection.scss";
// Assuming you have this component for animations
import AnimatedSection from "../../components/Ui/AnimatedSection";
// Using modern icons for the form
import { Send, Smartphone, Mail, User, MessageSquare } from "lucide-react";
import { Link } from "react-router-dom";

const HeroSection = () => {
  // Keeping the parallax effect logic for the left content
  const [offset, setOffset] = useState(0);
  const [playVideo, setPlayVideo] = useState(false);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") setPlayVideo(false);
    };

    if (playVideo) {
      document.addEventListener("keydown", handleEsc);
    }

    return () => document.removeEventListener("keydown", handleEsc);
  }, [playVideo]);

  // State for the "Get Quote" form data
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    message: "",
  });

  // Scroll effect for parallax
  useEffect(() => {
    const handleScroll = () => setOffset(window.scrollY * 0.2);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Handler for updating form fields
  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Handler for form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("Quote Request Submitted:", formData);
    // TODO: Integrate with your backend/API to send the email/data
    alert(
      "Thank you! We received your quote request and will contact you soon."
    );
    // Reset form after submission
    setFormData({ name: "", email: "", mobile: "", message: "" });
  };

  return (
    <section id="home" className="hero-section">
      {/* Redesigned Background Effect with subtle gradients */}
      <div className="hero-bg-effect" aria-hidden="true" />

      <div className="hero-container">
        {/* The main two-column grid */}
        <div className="hero-grid">
          {/* ==================================== */}
          {/* LEFT COLUMN: Content (Parallax applied) */}
          {/* ==================================== */}
          <div
            className="hero-left-content"
            style={{ transform: `translateY(${offset * 0.4}px)` }}
          >
            <AnimatedSection>
              <div className="hero-badge">
                IT Consulting &amp; Digital Transformation
              </div>
            </AnimatedSection>

            <AnimatedSection delay={150}>
              <h1 className="hero-title">
                High-Impact{" "}
                <span className="hero-gradient-text">IT Solutions</span> for
                Your Business Growth.
              </h1>
            </AnimatedSection>

            <AnimatedSection delay={300}>
              <p className="hero-description">
                We are a leading technology solutions provider doing business
                worldwide <strong>for over 13+ years.</strong>.
              </p>

              {/* CENTERED VIDEO & BUTTONS GROUP */}
              <div className="hero-action-group">
                <div className="video-thumbnail">
                  <div
                    className="play-button-container"
                    onClick={() => setPlayVideo(true)}
                  >
                    <button className="play-button" aria-label="Play Video">
                      <div className="play-icon"></div>
                    </button>
                  </div>
                </div>

                <div className="hero-cta">
                  <Link to="contact-us" className="hero-btn-primary">
                    Start Your Project
                  </Link>
                  <button
                    className="hero-btn-secondary"
                    onClick={() => {
                      const section = document.getElementById("services");
                      if (section) {
                        section.scrollIntoView({
                          behavior: "smooth",
                          block: "start",
                          inline: "nearest",
                        });
                      }
                    }}
                  >
                    View Services
                  </button>
                </div>
              </div>
            </AnimatedSection>
          </div>

          {/* ==================================== */}
          {/* RIGHT COLUMN: Get Quote Form */}
          {/* ==================================== */}
          <AnimatedSection delay={200}>
            <div className="hero-form-card">
              <h2 className="form-title">Get a Free Quote</h2>
              <p className="form-subtitle">
                Tell us about your project and we'll be in touch within 24
                hours.
              </p>
              <form onSubmit={handleSubmit} className="quote-form">
                <div className="form-group">
                  <User className="icon" size={20} />
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleFormChange}
                    placeholder="Name"
                    required
                  />
                </div>

                <div className="form-group">
                  <Mail className="icon" size={20} />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleFormChange}
                    placeholder="Email"
                    required
                  />
                </div>

                <div className="form-group">
                  <Smartphone className="icon" size={20} />
                  <input
                    type="tel"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleFormChange}
                    placeholder="Mobile Number "
                    required
                  />
                </div>

                <div className="form-group textarea-group">
                  <MessageSquare className="icon-textarea" size={20} />
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleFormChange}
                    placeholder="Tell us briefly about your project/need..."
                    rows="2"
                    required
                  />
                </div>

                <button type="submit" className="submit-btn">
                  Send Quote Request <Send size={20} />
                </button>
              </form>
            </div>
          </AnimatedSection>
        </div>
      </div>
      {playVideo && (
        <div className="video-modal" onClick={() => setPlayVideo(false)}>
          <div
            className="video-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              className="video-close-btn"
              onClick={() => setPlayVideo(false)}
              aria-label="Close video"
            >
              ✕
            </button>

            <iframe
              src="https://www.youtube.com/embed/uqZPq6mZhSI?autoplay=1"
              title="YouTube Video"
              frameBorder="0"
              allow="autoplay; encrypted-media; fullscreen"
              allowFullScreen
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default HeroSection;